import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Tooltip, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import { FOCUS_CART_SHORTCUT, ShortcutHint } from '@/components/shortcuts';

type CartHeaderProps = {
    cartCount: number;
    isEmpty: boolean;
    /** The clear-cart control's press: opens the void confirm, never clears directly. */
    onClear: () => void;
};

/** The cart's title row: its item count, the `F4` hint (dimmed while the cart is empty and `F4` has no line to focus), and — with lines in the cart — the clear-cart control. */
export const CartHeader: React.FC<CartHeaderProps> = ({
    cartCount,
    isEmpty,
    onClear,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
            <div className="flex items-center gap-2">
                <Icon
                    className="size-4 text-accent"
                    icon="solar:cart-large-2-bold-duotone"
                />
                <Typography weight="semibold">{t('seller.cart')}</Typography>
                {cartCount > 0 ? (
                    <Chip color="accent" size="sm" variant="soft">
                        {cartCount}
                    </Chip>
                ) : null}
                <ShortcutHint
                    isDisabled={isEmpty}
                    shortcut={FOCUS_CART_SHORTCUT}
                />
            </div>
            {isEmpty ? null : (
                <Tooltip delay={0}>
                    <Button
                        aria-label={t('seller.clearCart')}
                        {...getNodeTestIdProp('seller-cart-clear')}
                        isIconOnly
                        onPress={onClear}
                        size="sm"
                        variant="ghost"
                    >
                        <Icon
                            className="size-4 text-muted"
                            icon="solar:trash-bin-trash-linear"
                        />
                    </Button>
                    <Tooltip.Content>{t('seller.clearCart')}</Tooltip.Content>
                </Tooltip>
            )}
        </div>
    );
};
